"use client";

import * as React from "react";
import type { Column, Table } from "@tanstack/react-table";
import { Columns3, Eye } from "lucide-react";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@aio/platform-ui";

/**
 * Props for {@link ColumnVisibility}.
 */
export interface ColumnVisibilityProps<TData> {
  /** A configured TanStack Table instance. */
  table: Table<TData>;
  /** Override for the toolbar trigger button label. Defaults to "Columns". */
  triggerLabel?: string;
  /** Extra classes for the trigger button. */
  className?: string;
  /**
   * If true, renders a "Show all" affordance at the top of the menu. Defaults
   * to true. Hidden when every column is already visible.
   */
  showResetAction?: boolean;
}

/**
 * Display the visible label for a column, falling back to its id when
 * `header` is a render function (TanStack accepts ReactNode | function).
 */
function readColumnLabel<TData>(column: Column<TData, unknown>): string {
  const h = column.columnDef.header;
  if (typeof h === "string") return h;
  return column.id;
}

/**
 * Toolbar dropdown listing every hideable column with a checkbox per entry.
 *
 * Behaviour:
 * - Trigger is a "Columns" button that opens the menu.
 * - One `DropdownMenuCheckboxItem` per `column.getAllLeafColumns()` entry that
 *   reports `getCanHide() === true`. Columns without `getCanHide` (e.g. the
 *   selection column) are filtered out entirely.
 * - `checked` mirrors `column.getIsVisible()`; `onCheckedChange` calls
 *   `column.toggleVisibility()` so the table engine owns the state.
 * - When at least one column is hidden, a "Show all" action restores
 *   visibility via `table.toggleAllColumnsVisible(true)`.
 */
export function ColumnVisibility<TData>({
  table,
  triggerLabel,
  className,
  showResetAction = true,
}: ColumnVisibilityProps<TData>): React.JSX.Element {
  const hideableColumns = table
    .getAllLeafColumns()
    .filter((c) => c.getCanHide());
  const allVisible = hideableColumns.every((c) => c.getIsVisible());

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("gap-2", className)}
          data-testid="column-visibility-trigger"
        >
          <Columns3 className="size-4" aria-hidden="true" />
          <span>{triggerLabel ?? "Columns"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.getIsVisible()}
            onCheckedChange={(value) => column.toggleVisibility(!!value)}
            // Keep the menu open on toggle so the user can flip several
            // columns in one session without re-opening.
            onSelect={(e) => e.preventDefault()}
            data-testid={`column-visibility-toggle-${column.id}`}
          >
            {readColumnLabel(column)}
          </DropdownMenuCheckboxItem>
        ))}
        {showResetAction && !allVisible ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => table.toggleAllColumnsVisible(true)}
              data-testid="column-visibility-show-all"
            >
              <Eye className="size-4" aria-hidden="true" />
              <span>Show all</span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
